import { Link } from "wouter";
import { Linkedin, Github, Youtube, Twitter, ShieldCheck } from "lucide-react";
import { scrollToTop } from "@/lib/scroll";

const footerSections = [
  {
    title: "Product",
    links: [
      { label: "Security", href: "/security" },
      { label: "Inheritance", href: "/inheritance" },
      { label: "Watchtower", href: "/watchtower" },
      { label: "How It Works", href: "/how-it-works" },
      { label: "Pricing", href: "/pricing" },
      { label: "Compare Providers", href: "/compare" },
    ],
  },
  {
    title: "Free Tools",
    links: [
      { label: "Inheritance Scorecard", href: "/bitcoin-inheritance-scorecard" },
      { label: "Bitcoin IHT Calculator", href: "/bitcoin-inheritance-tax-calculator" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "For families & executors", href: "/recovery" },
      { label: "UK Inheritance Guide", href: "/insights/crypto-inheritance-planning-uk" },
      { label: "UK Checklist", href: "/crypto-inheritance-checklist-uk" },
      { label: "Executor Checklist", href: "/executor-crypto-checklist" },
      { label: "Insights", href: "/insights" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Book a call", href: "/book" },
      { label: "Contact", href: "/contact" },
      { label: "For professionals", href: "/advisers" },
      { label: "Partner Programme", href: "/partners" },
      { label: "System status", href: "/status" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms", href: "/terms" },
      { label: "Privacy", href: "/privacy" },
      { label: "Cookies", href: "/cookies" },
      { label: "Risk Disclosure", href: "/risk" },
    ],
  },
];

const socialLinks = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/bitzo1", testid: "footer-link-linkedin", icon: Linkedin },
  { label: "GitHub", href: "https://github.com/bitzo-crypto", testid: "footer-link-github", icon: Github },
  { label: "YouTube", href: "https://www.youtube.com/@Bitzo-crypto", testid: "footer-link-youtube", icon: Youtube },
  { label: "X", href: "https://x.com/Bitzocrypto", testid: "footer-link-x", icon: Twitter },
];

const socialIconClass =
  "w-9 h-9 rounded-lg border border-slate-800 flex items-center justify-center text-slate-400 hover:text-teal-300 hover:border-teal-500 hover:bg-teal-500/10 transition focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-slate-950";

export default function SiteFooter() {
  return (
    <footer className="bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand block */}
          <div className="lg:col-span-3 max-w-xs">
            <Link href="/">
              <img
                onClick={() => scrollToTop()}
                src="/assets/images/bitzo-logo.svg"
                alt="Bitzo"
                className="h-8 w-auto mb-5 cursor-pointer"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              UK-based cryptocurrency security and inheritance coordination.
            </p>
            <div className="flex items-center gap-2 mb-5">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Bitzo on ${social.label}`}
                    className={socialIconClass}
                    data-testid={social.testid}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </a>
                );
              })}
              <a
                href="https://www.crunchbase.com/organization/bitzo"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bitzo on Crunchbase"
                className={socialIconClass}
                data-testid="footer-link-crunchbase"
              >
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 6a6 6 0 1 0 0 12" />
                  <path d="M15 6v12" />
                  <path d="M15 6h2a3 3 0 0 1 0 6h-2" />
                  <path d="M15 12h2a3 3 0 0 1 0 6h-2" />
                </svg>
              </a>
            </div>
            <a
              href="/privacy"
              className="inline-flex items-center gap-2 border border-slate-800 rounded-lg px-3 py-1.5 text-xs uppercase tracking-wide text-slate-500 hover:text-slate-300 hover:border-slate-700 transition"
              data-testid="footer-gdpr-badge"
            >
              <img
                src="/assets/badges/gdpr-badge.svg"
                alt="GDPR"
                className="h-4 w-auto"
                data-testid="footer-gdpr-badge-img"
              />
              GDPR compliant
            </a>
          </div>

          {/* Link columns, own responsive grid so 5 columns flow neatly at every size */}
          <div className="lg:col-span-9 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8">
            {footerSections.map((section) => (
              <div key={section.title}>
                <h3 className="text-slate-200 font-semibold text-xs uppercase tracking-wider mb-4">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <Link href={link.href}>
                        <span
                          onClick={() => scrollToTop()}
                          className="text-slate-400 text-sm hover:text-white transition cursor-pointer"
                          data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {link.label}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legal strip */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-2 text-slate-300 text-sm mb-4">
            <ShieldCheck className="w-4 h-4 text-teal-400 flex-shrink-0" />
            <span>
              Bitzo never asks for seed phrases or private keys. We coordinate non-custodial security and recovery workflows.
            </span>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-start gap-3">
            <p className="text-slate-500 text-xs leading-relaxed">
              Bitzo is a trading name of Raplin Ventures Ltd, registered in England and Wales
              (company no. 17276917). Registered office: 71-75 Shelton Street, Covent Garden, London WC2H 9JQ.
              Bitzo is not authorised or regulated by the Financial Conduct Authority.{" "}
              <a href="/risk" className="underline hover:text-slate-300">See our Risk Disclosure</a>.
            </p>
            <p className="text-slate-500 text-xs md:text-right whitespace-nowrap">
              Digital assets are high risk. Not investment advice.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
